import { FC, useEffect, useState } from 'react';
import { Box, Chip, LinearProgress, Stack } from '@mui/material';
import { Slideshow } from '@mui/icons-material';

import { AlertBar } from '../../components/AlertBar';
import DraftPublishSwitcher from './components/DraftPublishSwitcher';
import Breadcrumbs from '../../components/Breadcrumb';
import { Mode } from './types';
import usePresentationApi from './hooks/usePresentationApi';
import { useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import MetaPreviewSection from './components/MetaPreviewSection';
import MetaDataPopover from './components/MetaDataPopover';
import ActionButtonSection from './components/ActionButtonSection';
import GuideText from './components/GuideText';
import { PreviewList } from '../presentations/PreviewList';
import { TRANSITIONS } from '../../components/preview/constants';
import { useDraftEdit } from './hooks/useDraftEdit';
import { tranformDetailToSlide } from './utils/tranformDetailToSlide';

const LoadingContainer = styled.div`
  height: 1px;
`;

const MainContainer = styled(Box)`
  width: 100%;
`;

const PageContainer = styled(Box)`
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
`;

const PresentationEditorPage: FC = () => {
  const params = useParams<{ id: string }>();
  const draftId = params.id || '';
  const { state } = useLocation();

  const [mode, setMode] = useState(Mode.Draft);
  const isPublishedMode = mode === Mode.Published;
  const {
    isLoading,
    draftData,
    errMessage,
    discardDraft,
    publishPresentation,
    refetchDraft,
    updatePresentation,
  } = usePresentationApi(draftId || '');

  const editOptions = useDraftEdit({
    updatePresentation,
    draftData,
  });

  const currenSlides = isPublishedMode
    ? tranformDetailToSlide(draftData?.detailedPresentation)
    : draftData?.slides || []; // either draft or published slides

  useEffect(() => {
    if (draftData) {
      document.title = `Presentation - ${draftData?.name} ${
        isPublishedMode ? '' : '(Draft)'
      }`;
    }
  }, [draftData]);

  const updatedKvs =
    (isPublishedMode ? draftData?.kvs : draftData?.detailedPresentation?.kvs) ||
    {};

  const presentationPageUrl = state?.search
    ? `/presentations${state?.search}`
    : '/presentations';

  return (
    <PageContainer>
      <Breadcrumbs
        items={[
          {
            label: 'Presentations',
            to: presentationPageUrl,
          },
          {
            label: draftData?.name || '-',
            to: `/presentations-editor/${draftId}`,
          },
        ]}
      />
      <LoadingContainer>{isLoading && <LinearProgress />}</LoadingContainer>

      <AlertBar severity="error" msg={errMessage} />

      <DraftPublishSwitcher
        onChange={(v) => setMode(v)}
        onDiscard={() => discardDraft()}
        onPublish={async () => {
          if (draftId) {
            await publishPresentation({ id: draftId });
            refetchDraft();
          }
        }}
        haveUnpublishedChanges={Boolean(draftData?.haveUnpublishedChanges)}
        isPublishedMode={isPublishedMode}
        hasPublishedVersion={Boolean(draftData?.detailedPresentation?._id)}
        previewAction={
          draftData ? (
            <MetaPreviewSection
              isPublishedMode={isPublishedMode}
              hasSlides={Boolean(currenSlides.length)}
              draftId={draftId}
              publishedId={draftData?.detailedPresentation?._id}
            />
          ) : undefined
        }
        summaryContent={
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Chip
              icon={<Slideshow sx={{ color: '#4f46e5 !important' }} />}
              label={`${currenSlides.length} ${
                currenSlides.length === 1 ? 'slide' : 'slides'
              }`}
              size="small"
              sx={{ bgcolor: '#f5f3ff', color: '#312e81', fontWeight: 700 }}
            />
            {draftData && <MetaDataPopover data={draftData} />}
          </Stack>
        }
        primaryAction={
          <ActionButtonSection
            isPublishedMode={isPublishedMode}
            publishedId={draftData?.detailedPresentation?._id || ''}
            onRefetch={() => refetchDraft()}
            handleAddSlide={editOptions.handleAddSlide}
          />
        }
      />
      <MainContainer>
        <Stack gap={2}>
          {!isLoading && (
            <GuideText
              isPublishedMode={isPublishedMode}
              hasSlides={Boolean(currenSlides.length)}
            />
          )}
          <PreviewList
            {...editOptions}
            slides={currenSlides}
            initialTransitions={TRANSITIONS}
            setSlides={(data) => {
              // TODO: remove console log after fixing this
              // eslint-disable-next-line no-console
              console.log(data);

              // TODO: Fix this smooth the onSlide ordering, get setCachedDraft from usePresentationApi
              // setCachedDraft(data);
            }}
            refetchPresentation={() => refetchDraft()}
            isPublished={isPublishedMode}
            updatedKvs={updatedKvs}
          />
        </Stack>
      </MainContainer>
    </PageContainer>
  );
};

export default PresentationEditorPage;
